import type { Cell, StageRecord } from "../game/types";
import { ITEMS } from "../game/items";
import { MONSTERS } from "../game/monsters";
import { bounding } from "../game/geometry";
import { rankFor } from "../game/scoring";
import { state } from "../game/state";
import { $ } from "../dom";
import { solvePacking } from "@jpjj/solver-core";
import type { PackItem, PackResult } from "@jpjj/solver-core/types";
import { sfxReveal, sfxPlaceTick, sfxWin } from "../audio/sfx";

/* ---------- Per-stage solves (loot + grid are fixed, so cache them) ---------- */
function stageItems(k: number): PackItem[] {
  return MONSTERS[k].loot.map((key) => ({
    key,
    value: ITEMS[key].value,
    cells: ITEMS[key].cells.map((c) => [...c] as Cell),
  }));
}

const solvePromises: (Promise<PackResult> | null)[] = MONSTERS.map(() => null);
const solveResults: (PackResult | null)[] = MONSTERS.map(() => null);

function solveStage(k: number): Promise<PackResult> {
  let pr = solvePromises[k];
  if (!pr) {
    const g = MONSTERS[k].grid;
    pr = solvePacking({ cols: g.cols, rows: g.rows, items: stageItems(k), timeLimitSec: 15 }).then((res) => {
      solveResults[k] = res;
      return res;
    });
    solvePromises[k] = pr;
  }
  return pr;
}

/** Kick off all five stage solves and resolve when every one is done. */
export function prepareSolves(): Promise<PackResult[]> {
  return Promise.all(MONSTERS.map((_, k) => solveStage(k)));
}

/* ---------- Comparison carousel ---------- */
let cmpIndex = 0;
let animCancel = false;
let animRunning = false;
let timers: number[] = [];
const sleep = (ms: number) =>
  new Promise<void>((res) => { const t = window.setTimeout(res, ms); timers.push(t); });

function clearTimers(): void {
  for (const t of timers) clearTimeout(t);
  timers = [];
}

function cmpCellSize(cols: number): number {
  const avail = Math.min(window.innerWidth - 56, 660);
  const perGrid = (avail - 48) / 2; // room for two grids + the "vs" gap
  return Math.max(20, Math.min(46, Math.floor(perGrid / cols)));
}

function buildSlots(host: HTMLElement, cols: number, rows: number): void {
  host.innerHTML = "";
  for (let i = 0; i < cols * rows; i++) {
    const d = document.createElement("div");
    d.className = "slot";
    host.appendChild(d);
  }
}

function buildPieceEl(key: string, offsets: Cell[]): HTMLElement {
  const item = ITEMS[key];
  const el = document.createElement("div");
  el.className = "piece opt-piece rarity-" + item.rarity;
  el.innerHTML = `<div class="pshape"></div><div class="picon">${item.icon}</div><div class="pval">${item.value}</div>`;
  const { h, w } = bounding(offsets);
  const shape = el.querySelector(".pshape") as HTMLElement;
  shape.style.gridTemplateColumns = `repeat(${w},1fr)`;
  shape.style.gridTemplateRows = `repeat(${h},1fr)`;
  const filled = new Set(offsets.map(([r, c]) => r + "," + c));
  for (let r = 0; r < h; r++) for (let c = 0; c < w; c++) {
    const d = document.createElement("div");
    d.className = "pcell " + (filled.has(r + "," + c) ? "fill" : "empty");
    shape.appendChild(d);
  }
  return el;
}

function sizePieceEl(el: HTMLElement, offsets: Cell[], r: number, c: number, oc: number): void {
  const { h, w } = bounding(offsets);
  el.style.width = w * oc + "px";
  el.style.height = h * oc + "px";
  el.style.left = c * oc + "px";
  el.style.top = r * oc + "px";
  const icon = el.querySelector(".picon") as HTMLElement;
  icon.style.fontSize = Math.min(Math.min(w, h) * oc * 0.62, oc * 1.05) + "px";
}

/** A normalized list of placements for both sides of one stage. */
interface Placed { key: string; r: number; c: number; cells: Cell[]; value: number }

// the placements currently on screen (for skip + resize re-fit)
let curYou: Placed[] = [];
let curOpt: Placed[] = [];
let curCols = 0;

function fromRecord(rec: StageRecord): Placed[] {
  return rec.pieces.map((p) => ({ key: p.key, r: p.r, c: p.c, cells: p.cells, value: p.value }));
}
function fromResult(res: PackResult): Placed[] {
  return res.placements.map((p) => ({ key: p.key, r: p.r, c: p.c, cells: p.cells, value: p.value }));
}
const byCell = (a: Placed, b: Placed) => a.r - b.r || a.c - b.c;

export function openComparison(): void {
  $("endOverlay").classList.add("hidden");
  $("finalView").style.display = "none";
  $("stageView").style.display = "";
  $("solverOverlay").classList.remove("hidden");
  sfxReveal();
  cmpIndex = 0;
  void runStageCompare(0);
}

async function runStageCompare(k: number): Promise<void> {
  clearTimers();
  animCancel = false;
  animRunning = true;

  const mon = MONSTERS[k];
  const rec = state.stages[k];
  const cols = rec.cols, rows = rec.rows;

  $("cmpHead").innerHTML = `Stage ${k + 1} / ${MONSTERS.length} — <span class="lv-mon">${mon.icon} ${mon.name}</span>`;
  ($("cmpNext") as HTMLButtonElement).disabled = true;
  $("cmpGap").innerHTML = "&nbsp;";
  $("youScore").textContent = "0";
  $("optScore").textContent = "0";

  // size + build both grids
  const oc = cmpCellSize(cols);
  document.documentElement.style.setProperty("--optcell", oc + "px");
  for (const id of ["youStage", "optStage"]) {
    const st = $(id);
    st.style.setProperty("--gcols", String(cols));
    st.style.setProperty("--grows", String(rows));
  }
  buildSlots($("youSlots"), cols, rows);
  buildSlots($("optSlots"), cols, rows);
  $("youPieces").innerHTML = "";
  $("optPieces").innerHTML = "";

  // ensure this stage is solved
  const status = $("solverStatus");
  status.textContent = `Solving stage ${k + 1} with HiGHS…`;
  let res: PackResult;
  try {
    res = await solveStage(k);
  } catch {
    status.textContent = "The solver could not be reached — check your connection and try again.";
    animRunning = false;
    return;
  }
  if (animCancel) return;
  status.textContent =
    res.status === "OPTIMAL"
      ? `HiGHS proved the optimum in ${Math.round(res.tSolveMs)} ms`
      : `HiGHS best found in ${Math.round(res.tSolveMs)} ms`;

  curYou = fromRecord(rec).sort(byCell);
  curOpt = fromResult(res).sort(byCell);
  curCols = cols;
  await animatePlacements(curYou, curOpt, oc);
  if (animCancel) return;

  finishStage(rec.value, res.value);
}

async function animatePlacements(you: Placed[], opt: Placed[], oc: number): Promise<void> {
  const n = Math.max(you.length, opt.length);
  let youRun = 0, optRun = 0;
  for (let i = 0; i < n; i++) {
    if (animCancel) return;
    if (i < you.length) {
      const p = you[i];
      const el = buildPieceEl(p.key, p.cells);
      $("youPieces").appendChild(el);
      sizePieceEl(el, p.cells, p.r, p.c, oc);
      youRun += p.value;
      $("youScore").textContent = String(youRun);
      sfxPlaceTick(false);
    }
    if (i < opt.length) {
      const p = opt[i];
      const el = buildPieceEl(p.key, p.cells);
      $("optPieces").appendChild(el);
      sizePieceEl(el, p.cells, p.r, p.c, oc);
      optRun += p.value;
      $("optScore").textContent = String(optRun);
      sfxPlaceTick(true);
    }
    await sleep(240);
  }
}

function placeAll(host: HTMLElement, list: Placed[], oc: number): number {
  host.innerHTML = "";
  let sum = 0;
  for (const p of list) {
    const el = buildPieceEl(p.key, p.cells);
    el.style.animation = "none";
    host.appendChild(el);
    sizePieceEl(el, p.cells, p.r, p.c, oc);
    sum += p.value;
  }
  return sum;
}

export function skipCompare(): void {
  if (!animRunning) return;
  animCancel = true;
  clearTimers();
  const rec = state.stages[cmpIndex];
  const res = solveResults[cmpIndex];
  if (!res) return;
  const oc = cmpCellSize(curCols);
  const youSum = placeAll($("youPieces"), curYou, oc);
  const optSum = placeAll($("optPieces"), curOpt, oc);
  $("youScore").textContent = String(youSum);
  $("optScore").textContent = String(optSum);
  finishStage(rec.value, res.value);
}

function finishStage(youVal: number, optVal: number): void {
  animRunning = false;
  const gap = optVal - youVal;
  $("cmpGap").textContent =
    gap <= 0
      ? "You matched the optimum on this stage — perfectly packed."
      : `You left ${gap} on the table here (${optVal > 0 ? Math.round((youVal / optVal) * 100) : 0}% of optimal).`;
  const next = $("cmpNext") as HTMLButtonElement;
  next.disabled = false;
  next.textContent = cmpIndex === MONSTERS.length - 1 ? "See your run ▸" : "Next stage ▸";
}

export function nextCompare(): void {
  if (animRunning) { skipCompare(); return; }
  if (cmpIndex < MONSTERS.length - 1) {
    cmpIndex++;
    void runStageCompare(cmpIndex);
  } else {
    showFinal();
  }
}

function showFinal(): void {
  const youTotal = state.stages.reduce((s, r) => s + r.value, 0);
  const optTotal = solveResults.reduce((s, r) => s + (r ? r.value : 0), 0);
  $("fcYou").textContent = String(youTotal);
  $("fcRank").textContent = rankFor(youTotal).split(" — ")[0];
  $("fcOpt").textContent = String(optTotal);
  const gap = optTotal - youTotal;
  $("fcGap").textContent =
    gap <= 0
      ? "A flawless run — across all five stages, nothing could be packed better."
      : `Across five stages you carried ${youTotal}. The optimizer carried ${optTotal} — ${gap} more, at ${optTotal > 0 ? Math.round((youTotal / optTotal) * 100) : 0}% of optimal.`;

  const rows = state.stages
    .map((rec, k) => {
      const mon = MONSTERS[k];
      const o = solveResults[k];
      const ov = o ? o.value : 0;
      return `<tr><td>${mon.icon} ${mon.name}</td><td>${rec.value}</td><td class="gold">${ov}</td><td>${ov > 0 ? Math.round((rec.value / ov) * 100) : 0}%</td></tr>`;
    })
    .join("");
  $("fcTable").innerHTML =
    `<thead><tr><th>Stage</th><th>You</th><th>Optimal</th><th>%</th></tr></thead><tbody>${rows}</tbody>`;

  $("stageView").style.display = "none";
  $("finalView").style.display = "";
  sfxWin();
}

/** Reset reveal UI between runs (the per-stage solves are reused — fixed loot). */
export function resetReveal(): void {
  animCancel = true;
  animRunning = false;
  clearTimers();
  cmpIndex = 0;
  $("youPieces").innerHTML = "";
  $("optPieces").innerHTML = "";
  $("stageView").style.display = "";
  $("finalView").style.display = "none";
}

/** Re-fit the comparison grids on window resize by re-placing at the new cell size. */
export function relayoutReveal(): void {
  if ($("finalView").style.display !== "none" || curCols === 0) return;
  const oc = cmpCellSize(curCols);
  document.documentElement.style.setProperty("--optcell", oc + "px");
  placeAll($("youPieces"), curYou, oc);
  placeAll($("optPieces"), curOpt, oc);
}
